import React, { useCallback } from 'react'
import { PageLayout } from '@/common/layouts/PageLayout.tsx'
import { PageTitle } from '@/common/components/PageTitle.tsx'
import { Button, Descriptions, DescriptionsProps, Flex, notification, Popconfirm, Spin, Tabs, Typography } from 'antd'
import { useNavigate, useParams } from 'react-router'
import { useStateWithSearchParams } from '@/common/hooks/useStateWithSearchParams.ts'
import TerminationInfo from '@/pages/offboarding/offboarding-details/TerminationInfo.tsx'
import ChecklistInfo from '@/pages/offboarding/offboarding-details/ChecklistInfo.tsx'
import TimeOffEntitlementInfo from './TimeOffEntitlementInfo'
import {
  useCompleteOffboardingRecordMutation,
  useDiscardOffboardingRecordMutation,
  useGetOffboardingRecordQuery,
} from '@/features/offboarding/offboarding.api.ts'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { EmployeePublicInfo } from '@/features/employee-data/employee-data.def.ts'
import { EmployeeTooltip } from '@/common/components/EmployeeTooltip.tsx'
import { handleApiError } from '@/utils/api.tsx'
import { routes } from '@/utils/routes.ts'
import { OffboardingStatus } from '@/features/offboarding/offboarding.def.ts'
import { OffboardingStatusTag } from '@/pages/offboarding/offboarding-details/OffboardingStatusTag.tsx'

const OffboardingDetailsPage: React.FC = () => {
  const { offboardingId } = useParams<{ offboardingId: string }>()
  const [tab, setTab] = useStateWithSearchParams<string>('tab', 'terminationInfo')
  const navigate = useNavigate()
  const offboardingRecordId = Number(offboardingId)
  const isValidOffboardingId = Number.isFinite(offboardingRecordId)
  const [discardOffboardingRecord, { isLoading: isDiscarding }] = useDiscardOffboardingRecordMutation()
  const [completeOffboardingRecord, { isLoading: isCompleting }] = useCompleteOffboardingRecordMutation()
  const {
    data: offboardingRecord,
    isFetching,
    isLoading: isOffboardingRecordsLoading,
    isError,
  } = useGetOffboardingRecordQuery(
    { offboardingRecordId },
    { skip: !isValidOffboardingId },
  )

  const { getEmployeePublicInfo, isLoading: isEmployeePublicInfoLoading } = useEmployeePublicInfo()

  const handleDiscard = useCallback(async () => {
    try {
      await discardOffboardingRecord({ offboardingRecordId }).unwrap()
      notification.success({ message: 'Offboarding record discarded successfully' })
      navigate(routes.offboardingList)
    } catch (error) {
      handleApiError(error, 'Failed to discard offboarding record')
    }
  }, [discardOffboardingRecord, navigate, offboardingRecordId])

  const handleComplete = useCallback(async () => {
    try {
      await completeOffboardingRecord({ offboardingRecordId }).unwrap()
      notification.success({ message: 'Offboarding record completed successfully' })
      navigate(routes.offboardingList)
    } catch (error) {
      handleApiError(error, 'Failed to complete offboarding record')
    }
  }, [completeOffboardingRecord, navigate, offboardingRecordId])

  if (!isValidOffboardingId) {
    throw new Error('Invalid offboard ID')
  }

  if (isOffboardingRecordsLoading || isFetching || isEmployeePublicInfoLoading) {
    return (
      <PageLayout>
        <Spin spinning />
      </PageLayout>
    )
  }

  if (isError || (!offboardingRecord && !isFetching)) {
    throw new Error('Could not find offboarding record')
  }

  const employeeInfo = getEmployeePublicInfo(offboardingRecord.employeeId)

  if (!employeeInfo) {
    throw new Error('Could not find employee info')
  }

  const descriptionItems = (data: EmployeePublicInfo): DescriptionsProps['items'] => [
    {
      key: 'employeeId',
      label: 'Employee ID',
      children: <EmployeeTooltip employeeId={data.employeeId} labelFn={x => x.employeeId} />,
    },
    {
      key: 'reportingManager',
      label: 'Reporting Manager',
      children: <EmployeeTooltip employeeId={data.reportingManager} />,
    },
    {
      key: 'status',
      label: 'Status',
      children: <OffboardingStatusTag data={offboardingRecord} />,
    },
  ]

  return (
    <PageLayout>
      <PageTitle value={`${employeeInfo.displayName} - Offboarding`} />
      <Spin spinning={isFetching}>
        <Flex vertical gap="middle">
          <Typography.Title level={2}>Offboarding - {`${employeeInfo.displayName}`}</Typography.Title>
          <Flex justify="space-between">
            <Descriptions items={descriptionItems(employeeInfo)} />
            <Flex gap="small">
              <Popconfirm
                title="Complete offboarding record"
                description="Are you sure you want to complete this offboarding record?"
                okText="Complete"
                okButtonProps={{ loading: isCompleting }}
                onConfirm={handleComplete}
              >
                <Button
                  type="primary"
                  loading={isCompleting}
                  disabled={offboardingRecord.status !== OffboardingStatus.InProgress}
                >
                  Complete Offboarding
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Discard offboarding record"
                description="Are you sure you want to discard this offboarding record?"
                okText="Discard"
                okButtonProps={{ danger: true, loading: isDiscarding }}
                onConfirm={handleDiscard}
              >
                <Button
                  danger
                  loading={isDiscarding}
                  disabled={offboardingRecord.status !== OffboardingStatus.InProgress}
                >
                  Discard
                </Button>
              </Popconfirm>
            </Flex>
          </Flex>
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              {
                key: 'terminationInfo',
                label: 'Termination Info',
                children: <TerminationInfo offboardingRecord={offboardingRecord} />,
              },
              {
                key: 'checklistInfo',
                label: 'Checklist Info',
                children: <ChecklistInfo offboardingRecord={offboardingRecord} />,
              },
              {
                key: 'timeOffEntitlement',
                label: 'Time Off Entitlement',
                children: <TimeOffEntitlementInfo
                  employeeId={employeeInfo.employeeId}
                  offboardingRecord={offboardingRecord}
                />,
              },
            ]}
          />
        </Flex>
      </Spin>
    </PageLayout>
  )
}

export default OffboardingDetailsPage